import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package2, ArrowLeft, Cookie, Globe, Settings, BarChart3, Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function CookiePolicy() {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <Package2 className="h-8 w-8 text-primary" />
              <span className="text-2xl font-heading font-bold">Even Rounds</span>
            </Link>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => i18n.changeLanguage(isEnglish ? 'fi' : 'en')}
              >
                <Globe className="h-4 w-4 mr-1" />
                {isEnglish ? 'FI' : 'EN'}
              </Button>
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t('common.back')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Cookie className="h-10 w-10 text-primary" />
            <h1 className="text-4xl font-heading font-bold">
              {isEnglish ? 'Cookie Policy' : 'Evästekäytäntö'}
            </h1>
          </div>
          <p className="text-muted-foreground">
            {isEnglish ? 'Last updated: December 2025' : 'Viimeksi päivitetty: Joulukuu 2025'}
          </p>
        </div>

        <div className="prose prose-stone max-w-none">
          {isEnglish ? (
            <>
              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">What Are Cookies?</h2>
                <p>
                  Cookies are small text files stored on your device when you visit websites. They help 
                  websites remember information about your visit, making your next visit easier and the 
                  site more useful to you.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="flex items-center gap-2 text-2xl font-heading font-semibold">
                  <Settings className="h-6 w-6 text-primary" />
                  Essential Cookies (Always Active)
                </h2>
                <p>
                  These cookies are necessary for the website to function properly. They enable basic 
                  features like page navigation and secure access. The website cannot function properly 
                  without these cookies.
                </p>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-stone-100">
                      <th className="border p-2 text-left">Cookie</th>
                      <th className="border p-2 text-left">Purpose</th>
                      <th className="border p-2 text-left">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border p-2">session_token</td>
                      <td className="border p-2">Maintains your login session</td>
                      <td className="border p-2">Session</td>
                    </tr>
                    <tr>
                      <td className="border p-2">cookie_consent</td>
                      <td className="border p-2">Stores your cookie preferences</td>
                      <td className="border p-2">1 year</td>
                    </tr>
                    <tr>
                      <td className="border p-2">i18next</td>
                      <td className="border p-2">Remembers your language preference</td>
                      <td className="border p-2">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </section>

              <section className="mb-8">
                <h2 className="flex items-center gap-2 text-2xl font-heading font-semibold">
                  <BarChart3 className="h-6 w-6 text-primary" />
                  Analytics Cookies (Optional)
                </h2>
                <p>
                  These cookies help us understand how visitors interact with our website by collecting 
                  and reporting information anonymously. This helps us improve our Service.
                </p>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-stone-100">
                      <th className="border p-2 text-left">Cookie</th>
                      <th className="border p-2 text-left">Purpose</th>
                      <th className="border p-2 text-left">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border p-2">_ga</td>
                      <td className="border p-2">Google Analytics - distinguishes users</td>
                      <td className="border p-2">2 years</td>
                    </tr>
                    <tr>
                      <td className="border p-2">_gid</td>
                      <td className="border p-2">Google Analytics - session tracking</td>
                      <td className="border p-2">24 hours</td>
                    </tr>
                  </tbody>
                </table>
              </section>

              <section className="mb-8">
                <h2 className="flex items-center gap-2 text-2xl font-heading font-semibold">
                  <Megaphone className="h-6 w-6 text-primary" />
                  Marketing Cookies (Optional)
                </h2>
                <p>
                  These cookies are used to track visitors across websites to display relevant 
                  advertisements. They are set by third-party advertising networks.
                </p>
                <p>
                  <strong>Note:</strong> Even Rounds currently does not use marketing/advertising cookies. 
                  If this changes, we will update this policy and request your consent.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">Third-Party Cookies</h2>
                <p>Some cookies are placed by third-party services we use:</p>
                <ul>
                  <li><strong>Google:</strong> For authentication (Google Sign-In)</li>
                  <li><strong>Stripe:</strong> For secure payment processing</li>
                </ul>
                <p>
                  These third parties have their own privacy policies governing their use of cookies.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">Managing Cookies</h2>
                <p>You can manage your cookie preferences in several ways:</p>
                <ul>
                  <li><strong>Cookie Banner:</strong> Use our cookie consent banner to enable/disable optional cookies</li>
                  <li><strong>Browser Settings:</strong> Most browsers allow you to refuse or delete cookies</li>
                  <li><strong>Opt-Out Tools:</strong> Use Google&apos;s opt-out browser add-on for Analytics</li>
                </ul>
                <p>
                  <strong>Note:</strong> Blocking essential cookies may prevent parts of the website from 
                  functioning correctly.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">Updates to This Policy</h2>
                <p>
                  We may update this Cookie Policy from time to time. The &quot;Last updated&quot; date at the 
                  top indicates when changes were last made.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">Contact Us</h2>
                <p>
                  If you have questions about our use of cookies:<br />
                  Vera Value Oy<br />
                  Email: <a href="mailto:admin@evenrounds.com">admin@evenrounds.com</a>
                </p>
              </section>
            </>
          ) : (
            <>
              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">Mitä evästeet ovat?</h2>
                <p>
                  Evästeet ovat pieniä tekstitiedostoja, jotka tallennetaan laitteeseesi verkkosivuilla 
                  vieraillessasi. Ne auttavat verkkosivustoja muistamaan tietoja vierailustasi, tehden 
                  seuraavasta vierailustasi helpomman ja sivustosta hyödyllisemmän sinulle.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="flex items-center gap-2 text-2xl font-heading font-semibold">
                  <Settings className="h-6 w-6 text-primary" />
                  Välttämättömät evästeet (Aina aktiiviset)
                </h2>
                <p>
                  Nämä evästeet ovat välttämättömiä verkkosivuston toiminnalle. Ne mahdollistavat 
                  perustoiminnot, kuten sivuilla liikkumisen ja turvallisen pääsyn. Verkkosivusto 
                  ei voi toimia kunnolla ilman näitä evästeitä.
                </p>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-stone-100">
                      <th className="border p-2 text-left">Eväste</th>
                      <th className="border p-2 text-left">Tarkoitus</th>
                      <th className="border p-2 text-left">Kesto</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border p-2">session_token</td>
                      <td className="border p-2">Ylläpitää kirjautumisistuntoasi</td>
                      <td className="border p-2">Istunto</td>
                    </tr>
                    <tr>
                      <td className="border p-2">cookie_consent</td>
                      <td className="border p-2">Tallentaa evästevalintasi</td>
                      <td className="border p-2">1 vuosi</td>
                    </tr>
                    <tr>
                      <td className="border p-2">i18next</td>
                      <td className="border p-2">Muistaa kielivalintasi</td>
                      <td className="border p-2">1 vuosi</td>
                    </tr>
                  </tbody>
                </table>
              </section>

              <section className="mb-8">
                <h2 className="flex items-center gap-2 text-2xl font-heading font-semibold">
                  <BarChart3 className="h-6 w-6 text-primary" />
                  Analytiikkaevästeet (Valinnainen)
                </h2>
                <p>
                  Nämä evästeet auttavat meitä ymmärtämään, miten kävijät käyttävät verkkosivustoamme 
                  keräämällä ja raportoimalla tietoja anonyymisti. Tämä auttaa meitä parantamaan 
                  Palveluamme.
                </p>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-stone-100">
                      <th className="border p-2 text-left">Eväste</th>
                      <th className="border p-2 text-left">Tarkoitus</th>
                      <th className="border p-2 text-left">Kesto</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border p-2">_ga</td>
                      <td className="border p-2">Google Analytics - erottaa käyttäjät</td>
                      <td className="border p-2">2 vuotta</td>
                    </tr>
                    <tr>
                      <td className="border p-2">_gid</td>
                      <td className="border p-2">Google Analytics - istuntoseuranta</td>
                      <td className="border p-2">24 tuntia</td>
                    </tr>
                  </tbody>
                </table>
              </section>

              <section className="mb-8">
                <h2 className="flex items-center gap-2 text-2xl font-heading font-semibold">
                  <Megaphone className="h-6 w-6 text-primary" />
                  Markkinointievästeet (Valinnainen)
                </h2>
                <p>
                  Näitä evästeitä käytetään kävijöiden seuraamiseen verkkosivustojen välillä 
                  relevanttien mainosten näyttämiseksi. Ne asetetaan kolmannen osapuolen 
                  mainosverkostojen toimesta.
                </p>
                <p>
                  <strong>Huomio:</strong> Even Rounds ei tällä hetkellä käytä markkinointi-/
                  mainosevästeitä. Jos tämä muuttuu, päivitämme tämän käytännön ja pyydämme 
                  suostumuksesi.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">Kolmannen osapuolen evästeet</h2>
                <p>Joitakin evästeitä asettavat käyttämämme kolmannen osapuolen palvelut:</p>
                <ul>
                  <li><strong>Google:</strong> Tunnistautumiseen (Google-kirjautuminen)</li>
                  <li><strong>Stripe:</strong> Turvalliseen maksunkäsittelyyn</li>
                </ul>
                <p>
                  Näillä kolmansilla osapuolilla on omat tietosuojakäytäntönsä evästeiden käytöstä.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">Evästeiden hallinta</h2>
                <p>Voit hallita evästevalintojasi useilla tavoilla:</p>
                <ul>
                  <li><strong>Evästebanneri:</strong> Käytä evästesuostumusbanneriamme ottaaksesi käyttöön/pois käytöstä valinnaisia evästeitä</li>
                  <li><strong>Selaimen asetukset:</strong> Useimmat selaimet sallivat evästeiden kieltämisen tai poistamisen</li>
                  <li><strong>Opt-Out-työkalut:</strong> Käytä Googlen opt-out-selaimen lisäosaa Analyticsille</li>
                </ul>
                <p>
                  <strong>Huomio:</strong> Välttämättömien evästeiden estäminen voi estää 
                  verkkosivuston osien toiminnan oikein.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">Käytännön päivitykset</h2>
                <p>
                  Voimme päivittää tätä evästekäytäntöä ajoittain. &quot;Viimeksi päivitetty&quot; -päivämäärä 
                  yläosassa kertoo, milloin muutoksia on viimeksi tehty.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">Yhteystiedot</h2>
                <p>
                  Jos sinulla on kysyttävää evästeiden käytöstämme:<br />
                  Vera Value Oy<br />
                  Sähköposti: <a href="mailto:admin@evenrounds.com">admin@evenrounds.com</a>
                </p>
              </section>
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-stone-100 bg-white mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Package2 className="h-6 w-6 text-primary" />
            <span className="text-xl font-heading font-bold">Even Rounds</span>
          </div>
          <p className="text-sm text-stone-500">
            © 2026 Vera Value Oy. {isEnglish ? 'All rights reserved.' : 'Kaikki oikeudet pidätetään.'}
          </p>
        </div>
      </footer>
    </div>
  );
}
